/**
 * splitModuleTitle — derive a card's display title + epithet subheading.
 *
 * Module names carry a mythic epithet ("Oracle - Keeper of the Lore",
 * "Atlas Painter - Bearer of Worlds"). Rendered as one line they overflow
 * the card header and get truncated. The card shows the base name on the
 * heading line and the epithet as a smaller subheading beneath it.
 *
 * The epithet is becoming a first-class `tagline` field on cms_products
 * (surfaced on the /api/products wire). Until every row is backfilled —
 * and for already-deployed SPAs reading an older wire — we fall back to
 * splitting a "Base - Epithet" name on its first " - " delimiter. Prefer
 * an explicit `tagline` whenever the wire carries one.
 */
export interface ModuleTitleParts {
  /** Heading line — the base product name. */
  title: string
  /** Optional subheading — the epithet / tagline. Omitted when there is none. */
  tagline?: string
}

const DELIMITER = ' - '

export function splitModuleTitle(
  name: string,
  tagline?: string | null,
): ModuleTitleParts {
  const explicit = tagline?.trim()
  if (explicit) {
    return { title: name.trim(), tagline: explicit }
  }
  const idx = name.indexOf(DELIMITER)
  if (idx > 0) {
    const title = name.slice(0, idx).trim()
    const epithet = name.slice(idx + DELIMITER.length).trim()
    if (title && epithet) {
      return { title, tagline: epithet }
    }
  }
  return { title: name.trim() }
}
